import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, API_BASE, ApiClientError } from '@/services/apiClient';
import { useHlsPlayer } from '@/hooks/useHlsPlayer';
import { useSportsVodResolveStatus } from '@/hooks/useSportsVodResolveStatus';
import { useWatchHistory } from '@/hooks/useWatchHistory';
import { useAppSelector } from '@/store';
import { lobbyWsClient } from '@/services/lobbyWs';
import {
  isKnownSportsVodSource,
  type SportsVodSource,
  type SportsVodTitle,
} from '@/types/sportsVod';
import { CustomPlayer } from '@/components/CustomPlayer/CustomPlayer';
import { QualityPanel } from '@/components/CustomPlayer/QualityPanel';
import { useHlsQuality } from '@/components/CustomPlayer/useHlsQuality';
import { useStreamDownload } from '@/components/CustomPlayer/useStreamDownload';
import { useFfrServers } from '@/hooks/useFfrServers';
import { FfrMultiServerPlayer } from '@/components/ffr/FfrMultiServerPlayer';

interface Props {
  id: string;
}

/**
 * History progress is posted at most once per `HISTORY_THROTTLE_MS`. Same
 * cadence Watch.tsx uses for movies/TV — keeps the POST volume to <1/sec
 * even if `timeupdate` fires every 250ms.
 */
const HISTORY_THROTTLE_MS = 10_000;

export function WatchSportsVod({ id }: Props): JSX.Element {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [title, setTitle] = useState<SportsVodTitle | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [lobbyAdded, setLobbyAdded] = useState(false);

  const { recordWatch } = useWatchHistory();
  const lobbyOpen = useAppSelector((s) => s.lobby.wsState === 'open');
  const inLobby = useAppSelector((s) => s.lobby.currentLobby != null);

  // Reset title only when navigating to a different id, not on retry.
  // Retry re-fetches the same id so the title text is stable across retries.
  useEffect(() => {
    setTitle(null);
    setLobbyAdded(false);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setNotFound(false);
    setFetchError(null);
    api<SportsVodTitle>(`/api/sports-vod/${encodeURIComponent(id)}`, { auth: true })
      .then((res) => {
        if (cancelled) return;
        setTitle(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiClientError && err.status === 404) {
          setNotFound(true);
        } else if (err instanceof Error) {
          setFetchError(err.message);
        } else {
          setFetchError('Failed to load title');
        }
      });
    return (): void => { cancelled = true; };
  }, [id, retryToken]);

  // URL `?source=` wins when valid. Otherwise fall back to the title's home
  // source so a fresh /watch/sports-vod/:id link plays immediately without
  // requiring the caller to know which catalog the entry came from.
  const rawSource = searchParams.get('source');
  const overrideSource: SportsVodSource | null = isKnownSportsVodSource(rawSource) ? rawSource : null;
  const activeSource: SportsVodSource | null = overrideSource ?? title?.source ?? null;

  // fullfightreplays multiserver: when the backend /servers endpoint is live
  // (STREAMING_FFR_CLIENT_MULTISERVER_ENABLED on) and returns a non-empty tree,
  // play client-side via sandboxed iframes (~zero residential) instead of the
  // legacy single-embed hls path. 404/unavailable → fall through unchanged.
  const ffr = useFfrServers(id, activeSource);
  const useFfrPlayer = ffr.status === 'ready' && (ffr.servers?.length ?? 0) > 0;
  // Hold the legacy hls/resolve path OFF while we're still deciding whether this
  // fullfightreplays title has a multiserver tree. Critical: starting the hls
  // path fires useSportsVodResolveStatus → /playlist.m3u8 preflight → a full
  // backend resolve chain (okru→byse→DM, possibly a browser). For an FFR title
  // that will end up on the iframe player, that expensive resolve is pure waste
  // and defeats the ~zero-residential goal. So while status is idle/loading for
  // an FFR title, we suppress it and only fall through to hls once we KNOW there
  // is no tree (status 'unavailable'/'error') or the title is non-FFR.
  const ffrDeciding = activeSource === 'fullfightreplays' && (ffr.status === 'idle' || ffr.status === 'loading');
  const hlsHeld = useFfrPlayer || ffrDeciding;

  const playlistUrl = useMemo(() => {
    if (!activeSource) return '';
    return `${API_BASE}/api/sports-vod-stream/${encodeURIComponent(id)}/playlist.m3u8?source=${encodeURIComponent(activeSource)}`;
  }, [id, activeSource]);

  // Cold-click pre-resolve gate (watchwrestling-style). For sources that
  // pre-resolve cache miss, the backend returns HTTP 202 + sseUrl; the hook
  // opens an EventSource and reports 'pending' until the resolver-executor
  // writes 'fresh', at which point we re-preflight and report 'ready'.
  // Sources that don't have a pre-resolve cache always hit 200 immediately
  // and the hook reports 'ready' on first preflight.
  const { state: resolveState, error: resolveError } = useSportsVodResolveStatus({
    // hlsHeld covers both the FFR iframe path and the still-deciding window, so
    // an FFR title never fires the /playlist.m3u8 preflight before the iframe
    // takes over.
    playlistUrl: hlsHeld ? '' : playlistUrl,
    retryToken,
  });

  const { fatal, hls } = useHlsPlayer({
    // Hold the player off until the resolve cache is ready. Passing '' keeps
    // hls.js dormant so we don't attempt a load that's known to 202. Also held
    // whenever hlsHeld (FFR iframe path or still-deciding).
    playlistUrl: hlsHeld ? '' : (resolveState === 'ready' ? playlistUrl : ''),
    videoRef,
    retryToken,
    // resolveState is in the key so useHlsPlayer's effect (keyed on `key`,
    // not playlistUrl) actually re-runs when the gate flips from pending
    // → ready and the real playlistUrl propagates in.
    key: `sports-vod|${id}|${activeSource ?? 'none'}|${resolveState}|${useFfrPlayer ? 'ffr' : 'hls'}`,
  });

  // Quality + client-side MP4 download — only on the hls.js path. The
  // progressive-MP4 sports source (hls == null) is already a direct file and is
  // out of scope here.
  const quality = useHlsQuality(hls);
  const download = useStreamDownload(hls, {
    title: title?.title ?? undefined,
    getDownloadLevel: () => quality.current,
  });

  // ---- history wiring ----
  // `recordWatch` posts on first frame (progress = 0) and then again, throttled,
  // as the user watches. We track the last-posted timestamp + the
  // first-frame flag in refs so re-renders don't reset them.
  const firstFrameSentRef = useRef(false);
  const lastPostedAtRef = useRef(0);
  const currentProgressRef = useRef(0);

  const postProgress = useCallback((progressMs: number): void => {
    recordWatch({ sportsVodId: id, progress: Math.max(0, Math.floor(progressMs)) });
    lastPostedAtRef.current = Date.now();
  }, [recordWatch, id]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !title) return;

    function onLoadedData(): void {
      if (firstFrameSentRef.current) return;
      firstFrameSentRef.current = true;
      postProgress(0);
    }

    function onTimeUpdate(): void {
      if (!video) return;
      const ms = Math.floor(video.currentTime * 1000);
      currentProgressRef.current = ms;
      const now = Date.now();
      if (now - lastPostedAtRef.current >= HISTORY_THROTTLE_MS) {
        postProgress(ms);
      }
    }

    function onPause(): void {
      // Flush immediately on pause — the throttle would otherwise lose the
      // last `<HISTORY_THROTTLE_MS` of progress if the user paused right
      // before unmount.
      if (currentProgressRef.current > 0) postProgress(currentProgressRef.current);
    }

    video.addEventListener('loadeddata', onLoadedData);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('pause', onPause);
    return (): void => {
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('pause', onPause);
      // Flush on unmount so navigating away preserves the latest position.
      if (firstFrameSentRef.current && currentProgressRef.current > 0) {
        postProgress(currentProgressRef.current);
      }
    };
  }, [title, postProgress]);

  function onAddToLobby(): void {
    if (!inLobby || !lobbyOpen) return;
    lobbyWsClient.sendNowPlaying(
      { kind: 'sports-vod', sportsVodId: id },
      activeSource ?? null,
    );
    setLobbyAdded(true);
  }

  if (notFound) {
    return (
      <main className="watch-page" data-testid="sports-vod-not-found">
        <div role="alert">{t('watch.sportsVod.notFound')}</div>
      </main>
    );
  }

  if (fetchError) {
    return (
      <main className="watch-page">
        <div className="watch-stage__fatal" role="alert" data-testid="sports-vod-fetch-error">
          <div>{t('watch.sportsVod.fetchError', { error: fetchError })}</div>
          <button type="button" onClick={() => setRetryToken((n) => n + 1)}>{t('common.retry')}</button>
        </div>
      </main>
    );
  }

  // Loading and ready states share one tree so the <video> element mounts
  // immediately. If we conditionally rendered the player only after the title
  // resolved, useHlsPlayer's effect would still run on first render but bail
  // (no videoRef.current) and never re-attach when the ref later wired up,
  // because effects don't re-run on ref changes. Keep the video mounted and
  // overlay the loading text instead.
  return (
    <main className="watch-page watch-page--sports-vod">
      <header className="watch-page__title">
        {title ? (
          <>
            <h1>{title.title}</h1>
            <span className="time-pill">{title.sport}</span>
            {activeSource && (
              <span className="time-pill" data-testid="sports-vod-source">{activeSource}</span>
            )}
          </>
        ) : (
          <h1 data-testid="sports-vod-loading">{t('common.loading')}</h1>
        )}
      </header>

      <div className="watch-stage">
        {useFfrPlayer && ffr.servers ? (
          <FfrMultiServerPlayer servers={ffr.servers} titleId={id} />
        ) : (
        <>
        {resolveState === 'pending' && (
          <div
            className="watch-stage__pending"
            role="status"
            aria-live="polite"
            data-testid="sports-vod-resolve-pending"
          >
            <div>{t('watch.sportsVod.pendingTitle')}</div>
            <div className="watch-stage__pending-sub">
              {t('watch.sportsVod.pendingBody')}
            </div>
          </div>
        )}
        {resolveState === 'failed' && (
          <div
            className="watch-stage__fatal"
            role="alert"
            data-testid="sports-vod-resolve-failed"
          >
            <div>{resolveError ? t('watch.sportsVod.resolveFailedWithError', { error: resolveError }) : t('watch.sportsVod.resolveFailed')}</div>
            <button type="button" onClick={() => setRetryToken((n) => n + 1)}>{t('common.retry')}</button>
          </div>
        )}
        <CustomPlayer
          videoRef={videoRef}
          fatal={fatal}
          onRetry={(): void => setRetryToken((n) => n + 1)}
          autoMuted={false}
          onUnmute={(): void => {}}
          tracks={[]}
          osBlobTrack={null}
          disableTopOverlay
          title={title?.title ?? undefined}
          download={hls ? { state: download.state, onStart: download.start, onCancel: download.cancel } : null}
          settingsPanels={{
            quality: quality.levels.length > 0 ? (
              <QualityPanel
                levels={quality.levels}
                current={quality.current}
                onSelect={quality.setLevel}
              />
            ) : undefined,
          }}
        />
        </>
        )}
      </div>

      {inLobby && (
        <div className="watch-page__lobby-actions">
          <button
            type="button"
            className="btn"
            onClick={onAddToLobby}
            disabled={!lobbyOpen || lobbyAdded}
            data-testid="sports-vod-add-to-lobby"
          >
            {lobbyAdded ? t('watch.sportsVod.addedToLobby') : t('watch.sportsVod.addToLobby')}
          </button>
        </div>
      )}
    </main>
  );
}
